import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpRequest } from '@angular/common/http';
import { Observable, Subscriber, interval } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ConfigurationService } from '../../services/configuration.service';

/** Where an upload is: sending the file (percent of the bytes), then the server's work (its phase and percent). */
export interface UploadState {
  phase: string;
  /** null while the server can't tell how far it is */
  percent: number;
  done: boolean;
  /** the upload's own result (as the plain upload returned it) */
  result?: any;
  error?: string;
}

/**
 * Uploads that run in the background on the server (HCO / business-unit bricks pattern): send the file with upload
 * progress, get { jobId }, then poll api/uploadJobs/{jobId} until it is done. Shown with crm-upload-progress.
 */
@Injectable({ providedIn: 'root' })
export class UploadJobService {
  constructor(private http: HttpClient, private configurations: ConfigurationService) { }

  /** POSTs the form with upload progress (the URL must answer { jobId } - add background=true where needed) */
  post(url: string, form: FormData): Observable<HttpEvent<any>> {
    return this.http.request(new HttpRequest('POST', url, form, { reportProgress: true }));
  }

  /** follows an upload request (HttpEvent stream answering { jobId }) to its end */
  run(request: Observable<HttpEvent<any>>): Observable<UploadState> {
    return new Observable<UploadState>(observer => {
      let poll = null;
      const sending = request.subscribe(event => {
        if (event.type === HttpEventType.UploadProgress)
          observer.next({ phase: 'Uploading the file', percent: event.total ? Math.round(100 * event.loaded / event.total) : null, done: false });
        else if (event.type === HttpEventType.Response) {
          const jobId = event.body && event.body.jobId;
          if (!jobId) { this.end(observer, event.body); return; }
          observer.next({ phase: 'Processing', percent: null, done: false });
          poll = this.follow(jobId, observer);
        }
      }, err => { observer.next({ phase: '', percent: null, done: true, error: UploadJobService.message(err, 'The upload failed.') }); observer.complete(); });
      return () => { sending.unsubscribe(); if (poll) poll.unsubscribe(); };
    });
  }

  /** follows a job that was already started (its id) */
  follow(jobId: string, observer: Subscriber<UploadState>) {
    let failures = 0;
    const sub = interval(1500).pipe(switchMap(() => this.http.get<any>(`${this.configurations.apiAddress}/api/uploadJobs/${jobId}`))).subscribe(status => {
      failures = 0;
      if (status.state === 'Completed') { sub.unsubscribe(); this.end(observer, status.result); return; }
      if (status.state === 'Failed') {
        sub.unsubscribe();
        observer.next({ phase: '', percent: null, done: true, error: status.errorMessage || 'The upload failed.' });
        observer.complete();
        return;
      }
      observer.next({ phase: status.phase, percent: status.totalRows > 0 ? Math.min(100, Math.round(100 * status.processedRows / status.totalRows)) : null, done: false });
    }, err => {
      if (++failures > 3) { observer.next({ phase: '', percent: null, done: true, error: UploadJobService.message(err, 'Lost track of the upload.') }); observer.complete(); }
    });
    return sub;
  }

  private end(observer: Subscriber<UploadState>, result: any) {
    if (typeof result === 'string') { try { result = JSON.parse(result); } catch { /* plain text result */ } }
    observer.next({ phase: 'Done', percent: 100, done: true, result });
    observer.complete();
  }

  static message(err: any, fallback: string): string {
    const body = err && err.error;
    if (body && typeof body === 'object' && (body.Message || body.message)) return body.Message || body.message;
    if (typeof body === 'string' && body.length < 500) return body;
    return fallback;
  }
}
